import { useRef, useState } from 'react'

import { OperationVariables, TypedDocumentNode } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import moment, { Moment } from 'moment'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import 'modules/core/hooks/useCurrentSession'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { dateUIToBackend } from 'modules/core/lib/dates'

import ScheduleActions from 'modules/project_management/components/Schedule/Actions'
import ScheduleFooter from 'modules/project_management/components/Schedule/Footer'
import { FooterElement } from 'modules/project_management/components/Schedule/Footer/index'
import ScheduleHeader from 'modules/project_management/components/Schedule/Header'
import { ScheduleVisibilityOptionsProvider } from 'modules/project_management/contexts/ScheduleVisibilyOptionsContext'

import ScheduleBody, { ScheduleParseDataType } from './Body'
import { ScheduleRecordValuesType } from './Body/ScheduleItem/types'
import RecurrentTypeModal from './RecurrentTypeModal'
import { RecurrentType } from './RecurrentTypeModal/RecurrentTypeSelector'
import ScheduleEmptyState from './ScheduleEmptyState'
import { ScheduleSidepanel } from './Sidepanel'

export type DateRange = [Date, Date]

interface ScheduleProps<TQuery, TVariables> {
  document: TypedDocumentNode<TQuery, TVariables>
  documentVariables: TVariables
  dataSource: (data: NonNullable<TQuery>) => {
    edges: Edge[]
    pageInfo: { hasNextPage: boolean; endCursor: string }
  }
  footerLegend?: FooterElement[]
  parseData: (data: TQuery | undefined, startDate: Moment, endDate: Moment) => ScheduleParseDataType
  showBetaTag?: boolean
  isMyProjectsView?: boolean
}

interface Edge {
  cursor: string
  node: unknown
}

const HEADER_AND_ACTIONS_HEIGHT = 226

const Schedule = <TQuery, TVariables extends OperationVariables>({
  document,
  documentVariables,
  dataSource,
  footerLegend,
  parseData,
  isMyProjectsView = false,
}: ScheduleProps<TQuery, TVariables>) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [range, setRange] = useState<DateRange>([
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate(),
  ])
  const [filters, setFilters] = useState<Record<string, number[]>>({})
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const vars = {
    ...documentVariables,
    startDate: range[0],
    endDate: range[1],
    ...filters,
    employeeName: debouncedSearch,
  }
  const onChangeFilter = (filterValue: Record<string, number[]>) => {
    setFilters((prev) => ({ ...prev, ...filterValue }))
  }

  const {
    loading,
    data,
    controls: { fetchMore, hasMore },
  } = usePaginatedQuery(document, {
    variables: { ...vars },
    extractConnection: dataSource,
  })

  const parsedData = parseData(data, moment(range[0]), moment(range[1]))
  const [openRecurrentTypeModal, setOpenRecurrentTypeModal] = useState(false)
  const [openAssignHoursSidepanel, setOpenAssignHoursSidepanel] = useState(false)
  const [sidePanelData, setSidePanelData] = useState<ScheduleRecordValuesType | null>(null)
  const [recurrentType, setRecurrentType] = useState<RecurrentType>('all')
  const [preselectedEmployee, setPreselectedEmployee] =
    useState<EmployeeForSelectFragmentNoAccess | null>(null)

  const closeAssignHoursSidepanel = () => {
    setOpenAssignHoursSidepanel(false)
  }

  const closeModalRecurrentType = () => {
    setOpenRecurrentTypeModal(false)
  }

  const onSubmitRecurrentType = (value: RecurrentType) => {
    setRecurrentType(value)
    closeModalRecurrentType()
    setOpenAssignHoursSidepanel(true)
  }

  const handleStartDateChange = (date: string) => {
    if (date) {
      const dateFormatted = moment(dateUIToBackend(date))
      const startOfMonth = dateFormatted.startOf('month').toDate()
      const endOfMonth = dateFormatted.endOf('month').toDate()
      setRange([startOfMonth, endOfMonth])
    }
  }

  const openSidepanel = (
    recordData?: ScheduleRecordValuesType | null,
    employee?: EmployeeForSelectFragmentNoAccess | null
  ) => {
    setSidePanelData(recordData || null)
    setPreselectedEmployee(employee || null)
    if (recordData?.id) {
      setOpenRecurrentTypeModal(true)
    } else {
      setOpenAssignHoursSidepanel(true)
    }
  }

  const isEmpty = parsedData.totalCount < 0
  return (
    <ScheduleVisibilityOptionsProvider>
      {isEmpty ? (
        <ScheduleEmptyState />
      ) : (
        <>
          <Box position="sticky" style={{ top: '0', zIndex: '4', backgroundColor: '#fff' }}>
            <ScheduleActions
              range={range}
              setRange={setRange}
              openSidepanel={openSidepanel}
              onChangeFilter={onChangeFilter}
              onSearch={onSearch}
              search={search}
              isMyProjectsView={isMyProjectsView}
            />
          </Box>
          <Box
            overflowY="auto"
            ref={scrollRef}
            style={{ maxHeight: `calc(100vh - ${HEADER_AND_ACTIONS_HEIGHT}px)` }}
          >
            <ScheduleHeader range={range} />
            <ScheduleBody
              loading={loading}
              parsedData={parsedData}
              fetchMore={fetchMore}
              hasMore={hasMore}
              range={range}
              scrollContainerRef={scrollRef}
              openSidepanel={openSidepanel}
              isMyProjectsView={isMyProjectsView}
            />
          </Box>
          {!isMyProjectsView && openRecurrentTypeModal && (
            <RecurrentTypeModal
              onClose={closeModalRecurrentType}
              onSubmit={onSubmitRecurrentType}
            />
          )}
          {!isMyProjectsView && openAssignHoursSidepanel && (
            <ScheduleSidepanel
              open={openAssignHoursSidepanel}
              onClose={closeAssignHoursSidepanel}
              onDateChange={handleStartDateChange}
              id={sidePanelData?.id}
              dailyMinutes={sidePanelData?.dailyMinutes}
              startDate={
                recurrentType === 'all' ? sidePanelData?.realStartDate : sidePanelData?.startDate
              }
              endDate={
                recurrentType === 'all' ? sidePanelData?.realEndDate : sidePanelData?.endDate
              }
              projectWorkerIds={
                sidePanelData?.projectWorkerId ? [sidePanelData?.projectWorkerId] : []
              }
              project={sidePanelData?.project}
              subproject={sidePanelData?.subproject}
              employee={sidePanelData?.employee || preselectedEmployee || null}
              weekDays={sidePanelData?.weekDays}
              recurrentType={recurrentType}
            />
          )}
          {footerLegend && <ScheduleFooter footerLegend={footerLegend} />}
        </>
      )}
    </ScheduleVisibilityOptionsProvider>
  )
}

export default Schedule
